/**
 * @module data/ai-layer
 *
 * The AI capability layer: how intelligent behaviour is designed, deployed and
 * governed across the enterprise.
 *
 * Three ideas are modelled here:
 *
 *  1. **The stack** — six layers from data foundation to business capability,
 *     with governance as a vertical that cuts through all of them.
 *  2. **The dots** — the many small points of intelligence distributed across
 *     the estate: in the cloud, at the edge, in the workflow, in the browser.
 *     Individually unremarkable, collectively the difference between an
 *     "AI project" and an AI-native enterprise.
 *  3. **The agents** — the role-based agentic workforce, each with a mandate,
 *     a tool boundary and a human accountable for it.
 *
 * All copy is data: the section renders whatever appears here.
 */

/**
 * @typedef {Object} AiStackLayer
 * @property {string}   id
 * @property {number}   index     0 = foundation.
 * @property {string}   name
 * @property {string}   short
 * @property {string}   body
 * @property {string[]} components  Concrete things that live in the layer.
 * @property {string}   accent
 * @property {string}   glyph
 */

/** @type {AiStackLayer[]} */
export const AI_STACK = [
  {
    id: "data-foundation",
    index: 0,
    name: "Data Foundation",
    short: "Data",
    body: "The layers below intelligence. Ownership, quality, semantics and lineage, published as contracts that anything above may consume without guessing.",
    components: [
      "Domain data contracts",
      "Entity ownership & stewardship",
      "Quality thresholds and monitors",
      "Lineage, provenance and retention",
    ],
    accent: "#6f9fbe",
    glyph: "database",
  },
  {
    id: "platform-runtime",
    index: 1,
    name: "Platform & Runtime",
    short: "Platform",
    body: "Where inference happens. Routing tiers, retrieval infrastructure, feature and vector stores, evaluation harnesses and the cost controls that make AI economically viable.",
    components: [
      "Model routing tiers",
      "Retrieval & vector infrastructure",
      "Feature and embedding stores",
      "Evaluation harness and golden sets",
    ],
    accent: "#8292ad",
    glyph: "server",
  },
  {
    id: "model-reasoning",
    index: 2,
    name: "Model & Reasoning",
    short: "Models",
    body: "The reasoning core: frontier and small models, fine-tuned specialists, structured output and the memory that lets a conversation become a relationship.",
    components: [
      "Frontier and small-model portfolio",
      "Domain fine-tunes and adapters",
      "Structured output and tool schemas",
      "Working, episodic and semantic memory",
    ],
    accent: "#a98bc4",
    glyph: "spark",
  },
  {
    id: "agentic-orchestration",
    index: 3,
    name: "Agentic Orchestration",
    short: "Agents",
    body: "Where models become colleagues. Role-based agents with a mandate, a tool boundary, a review loop and an accountable human owner.",
    components: [
      "Role-based agent definitions",
      "Tool and API boundaries",
      "Multi-agent hand-off protocols",
      "Human-in-the-loop review gates",
    ],
    accent: "#5fd4c4",
    glyph: "rocket",
  },
  {
    id: "experience",
    index: 4,
    name: "Experience",
    short: "Experience",
    body: "Where the capability meets a person: in the workflow, in the customer journey, in the browser, at the edge. Assistance that arrives in context rather than in another tab.",
    components: [
      "In-workflow assistance",
      "Customer-facing copilots",
      "Edge and on-device inference",
      "Progressive disclosure of autonomy",
    ],
    accent: "#d8a657",
    glyph: "compass",
  },
  {
    id: "business-capability",
    index: 5,
    name: "Business Capability",
    short: "Capability",
    body: "The point of it all: a capability the enterprise can now exercise faster, cheaper, more consistently or not at all — measured against the value case that funded it.",
    components: [
      "Capability-level outcome metrics",
      "Unit-cost and cycle-time baselines",
      "Adoption and confidence signals",
      "Value realisation reporting",
    ],
    accent: "#9db08a",
    glyph: "target",
  },
];

/**
 * The governance vertical that cuts through every layer.
 *
 * @typedef {Object} AiGuardrail
 * @property {string} id
 * @property {string} name
 * @property {string} body
 * @property {string} glyph
 */

/** @type {AiGuardrail[]} */
export const AI_GUARDRAILS = [
  {
    id: "evaluation",
    name: "Continuous evaluation",
    body: "Accuracy, refusal, fairness and cost are measured on every release against golden sets that live in version control.",
    glyph: "target",
  },
  {
    id: "explainability",
    name: "Explainability",
    body: "Every automated decision can be replayed: which sources were retrieved, which model answered, which rule applied.",
    glyph: "book",
  },
  {
    id: "observability",
    name: "Behavioural observability",
    body: "Drift, escalation and override rates are instrumented so silent capability decay is caught by monitoring, not by customers.",
    glyph: "pulse",
  },
  {
    id: "cost-control",
    name: "Cost control",
    body: "Per-transaction cost and energy budgets are enforced at the routing layer, with alerts before the invoice arrives.",
    glyph: "sliders",
  },
  {
    id: "human-accountability",
    name: "Human accountability",
    body: "No agent is deployed without a named human owner who can explain, suspend or retire it.",
    glyph: "people",
  },
  {
    id: "security",
    name: "Security & containment",
    body: "Tool boundaries, entitlement-aware retrieval and prompt-injection defences are designed in, then tested adversarially.",
    glyph: "server",
  },
];

/**
 * "AI dots": the distributed points of intelligence in the estate.
 *
 * @typedef {Object} AiDot
 * @property {string} id
 * @property {string} label
 * @property {string} where   Plane of the estate: cloud, edge, workflow, device.
 * @property {string} body
 * @property {string} glyph
 */

/** @type {AiDot[]} */
export const AI_DOTS = [
  {
    id: "cloud-reasoning",
    label: "Cloud reasoning",
    where: "Cloud",
    body: "Frontier and hosted models for complex reasoning, synthesis and long-context analysis where latency is affordable.",
    glyph: "server",
  },
  {
    id: "private-inference",
    label: "Private inference",
    where: "Cloud",
    body: "Models running inside your own boundary for classified data, regulated workloads and residency obligations.",
    glyph: "database",
  },
  {
    id: "workflow-copilot",
    label: "In-workflow copilot",
    where: "Workflow",
    body: "Assistance delivered inside the process a person is already in — drafting, checking, summarising, deciding.",
    glyph: "compass",
  },
  {
    id: "edge-sensing",
    label: "Edge sensing",
    where: "Edge",
    body: "Small models on factory floors, in stores and at network edges, turning signal into action without a round trip.",
    glyph: "pulse",
  },
  {
    id: "device-assist",
    label: "On-device assistance",
    where: "Device",
    body: "Local inference for privacy-sensitive, offline-capable assistance where the data should never leave the handset.",
    glyph: "plug",
  },
  {
    id: "background-agent",
    label: "Background agent",
    where: "Workflow",
    body: "Long-running agents that watch, assemble, reconcile and escalate — work that used to be somebody's Tuesday.",
    glyph: "rocket",
  },
  {
    id: "analytics-copilot",
    label: "Analytics copilot",
    where: "Cloud",
    body: "Natural-language inquiry over the governed metric layer, with every answer traceable back to its definition.",
    glyph: "sliders",
  },
  {
    id: "embedded-classifier",
    label: "Embedded classifier",
    where: "Edge",
    body: "Narrow models embedded in operational systems for triage, routing and quality control at machine speed.",
    glyph: "target",
  },
];

/**
 * Role-based agents. Each has a mandate, a boundary and an accountable human.
 *
 * @typedef {Object} AgentRole
 * @property {string} id
 * @property {string} name
 * @property {string} phaseId  The ADM phase the agent primarily supports.
 * @property {string} mandate  What the agent is for.
 * @property {string[]} tools  Systems and data it may touch.
 * @property {string} boundary The hard limit of its authority.
 */

/** @type {AgentRole[]} */
export const AGENT_ROLES = [
  {
    id: "discovery-agent",
    name: "Discovery analyst",
    phaseId: "vision",
    mandate:
      "Assembles the evidence pack for a value hypothesis: current metrics, comparable cases, data availability and the constraints that will shape the vision.",
    tools: ["Metric warehouse", "Requirements repository", "Document store"],
    boundary: "Read-only. May recommend; may not commit scope or budget.",
  },
  {
    id: "capability-agent",
    name: "Capability cartographer",
    phaseId: "business",
    mandate:
      "Keeps the capability map and value streams current by mining process logs and system events, flagging drift and proposing heat-map updates.",
    tools: ["Process mining", "Capability repository", "Ticketing"],
    boundary: "Proposes changes to the map. Business owners approve.",
  },
  {
    id: "data-agent",
    name: "Data contract steward",
    phaseId: "information-systems",
    mandate:
      "Drafts and checks data contracts, monitors quality thresholds and raises a breach before a downstream model is affected.",
    tools: ["Catalogue", "Quality monitors", "Schema registry"],
    boundary: "May publish a breach. May not change a contract without the domain owner.",
  },
  {
    id: "delivery-agent",
    name: "Delivery coordinator",
    phaseId: "migration",
    mandate:
      "Maintains the dependency graph across work packages, warns on change-load spikes and prepares the migration plan for review.",
    tools: ["Planning suite", "Change-load model", "Value register"],
    boundary: "Advisory. The PMO commits the plan.",
  },
  {
    id: "compliance-agent",
    name: "Architecture compliance reviewer",
    phaseId: "implementation-governance",
    mandate:
      "Reviews delivery artefacts against the architecture baseline, gathers evidence and prepares a compliance or dispensation recommendation.",
    tools: ["Architecture repository", "Pipeline metadata", "Evaluation results"],
    boundary: "Prepares the pack. The architecture board decides.",
  },
  {
    id: "drift-agent",
    name: "Architecture drift sentinel",
    phaseId: "change-management",
    mandate:
      "Watches production telemetry, model release notes and regulatory dockets for signals that invalidate an architectural assumption.",
    tools: ["Telemetry", "Release feeds", "Regulatory dockets"],
    boundary: "Escalates and summarises. Never changes a production configuration itself.",
  },
];

/**
 * The orchestration loop that drives the agentic workforce. Used by the
 * animated scene in the AI layer section.
 *
 * @type {string[]}
 */
export const AGENT_LOOP = ["Observe", "Retrieve", "Reason", "Act", "Review"];

/**
 * Aggregate the AI capability summary shown at the top of the section.
 */
export const AI_SUMMARY = {
  eyebrow: "The AI capability layer",
  title: "Intelligence, designed into the architecture",
  lede:
    "AI does not sit beside the architecture — it runs through it. These six layers turn scattered experiments into a governed capability, and the dots are where that capability actually touches the business.",
};

/**
 * Find a stack layer by id.
 *
 * @param {string} id
 * @returns {AiStackLayer|undefined}
 */
export function getLayerById(id) {
  return AI_STACK.find((layer) => layer.id === id);
}
